// Componente de presentación de la página de inicio
// Display component for the home page

import {
  DashboardHeader,
  OrderStatusMetrics,
  OrdersPreparedToday,
  OrdersWithErrors,
  InventoryMetrics,
  ShippedProducts,
  ChildTeamOrdersReport,
} from './';
import { useOrdersKpis, useErrorsKpis, useMerchandiseKpis, useProductsKpis } from '../../hooks';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useEffect, useRef, useMemo } from 'react';
import { PermissionGuard } from '@shared/components/auth/PermissionGuard';
import { HomeSkeleton } from '../skeletons';

export const Home = () => {
  // Fuente única de verdad: query params
  const { params, updateParam } = useQueryParams({
    shop_id: { type: 'string', default: 'all' },
    cutoff_time: { type: 'string', default: '14:00' },
  });

  // 🎯 Memorizar filtros para evitar recreación innecesaria
  const filters = useMemo(() => ({
    shop_id: params.shop_id !== 'all' ? parseInt(params.shop_id, 10) : undefined,
    cutoff_time: params.cutoff_time,
    date: undefined,
  }), [params.shop_id, params.cutoff_time]);

  // 🎯 Obtener datos de todos los hooks de KPIs
  // React Query automáticamente refetch cuando cambia el queryKey (filters)
  const { isLoading: ordersLoading, isFetching: ordersFetching } = useOrdersKpis(filters);
  const { isLoading: errorsLoading, isFetching: errorsFetching } = useErrorsKpis(filters);
  const { isLoading: merchandiseLoading, isFetching: merchandiseFetching } = useMerchandiseKpis(filters);
  const { isLoading: productsLoading, isFetching: productsFetching } = useProductsKpis(filters);

  // Mostrar spinner solo en la primera carga
  const hasLoadedRef = useRef(false);
  const allNotLoading = !(ordersLoading || errorsLoading || merchandiseLoading || productsLoading);
  useEffect(() => {
    if (!hasLoadedRef.current && allNotLoading) {
      hasLoadedRef.current = true;
    }
  }, [allNotLoading]);

  const showInitialSpinner = !hasLoadedRef.current && !allNotLoading;

  // En recargas por filtro: congelar contenido con opacidad
  const isAnyFetching = useMemo(
    () => ordersFetching || errorsFetching || merchandiseFetching || productsFetching,
    [ordersFetching, errorsFetching, merchandiseFetching, productsFetching]
  );

  return (
    <div className="min-h-screen bg-body-bg p-6">
      {/* Header del dashboard */}
      <DashboardHeader 
        shopId={params.shop_id}
        cutoffTime={params.cutoff_time}
        onChangeShop={(v) => updateParam('shop_id', v)}
        onChangeCutoffTime={(v) => updateParam('cutoff_time', v)}
      />

      {/* Skeleton mientras carga inicialmente */}
      {showInitialSpinner ? (
        <HomeSkeleton />
      ) : (
        <>
          {/* Layout principal en dos columnas - Solo visible cuando no está cargando */}
          {!showInitialSpinner && (
            <div 
              className={`grid grid-cols-1 lg:grid-cols-2 gap-6 transition-opacity duration-300 ${
                isAnyFetching ? 'opacity-50' : 'opacity-100'
              }`}
            >
          {/* Columna izquierda - Pedidos preparados, métricas de estado y productos enviados */}
          <div className="space-y-6">
            {/* Pedidos preparados hoy */}
            <OrdersPreparedToday filters={filters} />

            {/* Métricas de estado de pedidos */}
            <OrderStatusMetrics filters={filters} />
          </div>

          {/* Pedidos con errores */}
          <OrdersWithErrors filters={filters} />

          <div className="space-y-6">
            {/* Productos enviados */}
            <ShippedProducts filters={filters} />
          </div>

          <div className="space-y-6">
            {/* Entradas de mercancía */}
            <InventoryMetrics filters={filters} />
          </div>
        </div>
      )}

          {/* Reporte de pedidos por cliente hijo - Solo para tenant_3pl */}
          {!showInitialSpinner && (
            <PermissionGuard teamTypes={['tenant_3pl']}>
              <div 
                className={`mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 transition-opacity duration-300 ${
                  isAnyFetching ? 'opacity-50' : 'opacity-100'
                }`}
              >
                <ChildTeamOrdersReport />
              </div>
            </PermissionGuard>
          )}
        </>
      )}
    </div>
  );
};
